import {ArrowDown, FunnelSimple, MagnifyingGlass, X} from "@phosphor-icons/react";
import {useState} from "react";
import type {SelectOption} from "@/components";
import {BaseButton, BaseInput, BaseSelect} from "@/components";
import type {SortOption} from "./useGamesCatalog";

const SORT_OPTIONS: ReadonlyArray<SelectOption<string>> = [
  {value: "popular", label: "Populares"},
  {value: "recent", label: "Recientes"},
  {value: "az", label: "A–Z"},
  {value: "favorites", label: "Favoritos"},
];

interface FilterOption {
  value: string;
  label: string;
}

export interface CatalogToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;
  provider: string;
  onProviderChange: (v: string) => void;
  category: string;
  onCategoryChange: (v: string) => void;
  sort: SortOption;
  onSortChange: (v: SortOption) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  providerOptions: ReadonlyArray<FilterOption>;
  categoryOptions: ReadonlyArray<FilterOption>;
  onScrollToResults?: () => void;
}

const cardCls =
  "surface-elev-2 rounded-2xl border border-[#e91e8c55] p-4 flex flex-col gap-3 " +
  "shadow-[0_0_32px_-4px_#e91e8c55,0_0_8px_-2px_#e91e8c33] " +
  "relative overflow-hidden " +
  "before:absolute before:inset-0 before:rounded-2xl " +
  "before:bg-[radial-gradient(ellipse_80%_40%_at_50%_0%,#e91e8c18,transparent)] " +
  "before:pointer-events-none";

const selectLabelCls = "text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-tertiary)] px-1";

const CatalogToolbar = ({
  search,
  onSearchChange,
  provider,
  onProviderChange,
  category,
  onCategoryChange,
  sort,
  onSortChange,
  hasActiveFilters,
  onClearFilters,
  providerOptions,
  categoryOptions,
  onScrollToResults,
}: CatalogToolbarProps) => {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const allProviders: ReadonlyArray<SelectOption<string>> = [
    {value: "", label: "Todos los proveedores"},
    ...providerOptions,
  ];
  const allCategories: ReadonlyArray<SelectOption<string>> = [
    {value: "", label: "Todas las categorías"},
    ...categoryOptions,
  ];

  const activeCount = [provider, category, sort !== "popular" ? sort : ""].filter(Boolean).length;

  return (
    <div
      className={cardCls}
      role="search"
      aria-label="Filtros del catálogo de juegos"
    >
      {/* Search + filter toggle row */}
      <div className="flex gap-2 items-center">
        <BaseInput
          type="search"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") onScrollToResults?.();
          }}
          placeholder="Buscar juego por nombre…"
          leftIcon={<MagnifyingGlass size={16} aria-hidden="true" />}
          aria-label="Buscar juegos por nombre"
          fullWidth
          className={
            "focus-visible:outline-none " +
            "focus-visible:border-[#e91e8c] " +
            "focus-visible:shadow-[0_0_0_3px_#e91e8c33,0_0_28px_-2px_#e91e8caa,inset_0_0_18px_-6px_#e91e8c22]"
          }
        />
        {search.trim() && (
          <BaseButton
            variant="primary"
            size="sm"
            onClick={onScrollToResults}
            aria-label="Ir a resultados"
            leftIcon={<ArrowDown size={15} weight="bold" aria-hidden="true" />}
          >
            Ver
          </BaseButton>
        )}

        {/* Mobile filter toggle — hidden on desktop */}
        <button
          type="button"
          onClick={() => setFiltersOpen(v => !v)}
          aria-expanded={filtersOpen}
          aria-label="Mostrar filtros"
          className={
            "min-[640px]:hidden flex items-center gap-1.5 shrink-0 h-9 px-3 rounded-xl border text-xs font-semibold transition-all duration-200 " +
            (filtersOpen || activeCount > 0
              ? "border-[#e91e8c] bg-[rgba(233,30,140,0.12)] text-[#e91e8c]"
              : "border-[rgba(255,255,255,0.15)] bg-transparent text-[var(--color-text-tertiary)]")
          }
        >
          <FunnelSimple size={14} aria-hidden="true" />
          {activeCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-[#e91e8c] text-white text-[10px] font-bold flex items-center justify-center leading-none">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {/* Desktop filters — always visible on ≥640px */}
      <div className="hidden min-[640px]:flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-tertiary)] shrink-0 select-none mr-1">
          <FunnelSimple size={13} aria-hidden="true" />
          Filtros
        </span>
        <div className="flex flex-col gap-0.5 min-w-[140px] flex-1">
          <span className={selectLabelCls}>Proveedor</span>
          <BaseSelect<string>
            value={provider}
            onChange={e => { onProviderChange(e.target.value); onScrollToResults?.(); }}
            options={allProviders}
            aria-label="Filtrar por proveedor"
            size="sm"
            fullWidth
          />
        </div>
        <div className="flex flex-col gap-0.5 min-w-[120px] flex-1">
          <span className={selectLabelCls}>Categoría</span>
          <BaseSelect<string>
            value={category}
            onChange={e => { onCategoryChange(e.target.value); onScrollToResults?.(); }}
            options={allCategories}
            aria-label="Filtrar por categoría"
            size="sm"
            fullWidth
          />
        </div>
        <div className="flex flex-col gap-0.5 min-w-[110px] flex-1">
          <span className={selectLabelCls}>Ordenar</span>
          <BaseSelect<string>
            value={sort}
            onChange={e => { onSortChange(e.target.value as SortOption); onScrollToResults?.(); }}
            options={SORT_OPTIONS}
            aria-label="Ordenar juegos"
            size="sm"
            fullWidth
          />
        </div>
        {hasActiveFilters && (
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            aria-label="Limpiar todos los filtros activos"
            leftIcon={<X size={14} aria-hidden="true" />}
          >
            Limpiar
          </BaseButton>
        )}
      </div>

      {/* Mobile filters — collapsible panel */}
      {filtersOpen && (
        <div className="min-[640px]:hidden flex flex-col gap-2 pt-1 border-t border-[rgba(255,255,255,0.07)]">
          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col gap-0.5">
              <span className={selectLabelCls}>Proveedor</span>
              <BaseSelect<string>
                value={provider}
                onChange={e => { onProviderChange(e.target.value); onScrollToResults?.(); }}
                options={allProviders}
                aria-label="Filtrar por proveedor"
                size="sm"
                fullWidth
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className={selectLabelCls}>Categoría</span>
              <BaseSelect<string>
                value={category}
                onChange={e => { onCategoryChange(e.target.value); onScrollToResults?.(); }}
                options={allCategories}
                aria-label="Filtrar por categoría"
                size="sm"
                fullWidth
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className={selectLabelCls}>Ordenar</span>
              <BaseSelect<string>
                value={sort}
                onChange={e => { onSortChange(e.target.value as SortOption); onScrollToResults?.(); }}
                options={SORT_OPTIONS}
                aria-label="Ordenar juegos"
                size="sm"
                fullWidth
              />
            </div>
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => { onClearFilters(); setFiltersOpen(false); }}
              className="flex items-center gap-1 text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-danger-text)] transition-colors self-start px-1"
            >
              <X size={12} aria-hidden="true" />
              Limpiar filtros
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CatalogToolbar;

